import React from 'react';
import { Building2, FileText, DollarSign, TrendingUp, Info, Edit3 } from 'lucide-react';

const propertyLabels = {
  casa: {
    largo: "Largo de la Casa (m)*",
    ancho: "Ancho de la Casa (m)*",
    areaTotal: "Área Total de la Casa (m²)",
    areaOcupada: "Área Construida (m²)*"
  },
  departamento: {
    largo: "Largo del Departamento (m)*",
    ancho: "Ancho del Departamento (m)*",
    areaTotal: "Área Total del Departamento (m²)",
    areaOcupada: "Área Útil (m²)*"
  },
  terreno: {
    largo: "Largo del Terreno (m)*",
    ancho: "Ancho del Terreno (m)*",
    areaTotal: "Área Total del Terreno (m²)",
    areaOcupada: "Área Ocupada (m²)*"
  },
  local: {
    largo: "Largo del Local (m)*",
    ancho: "Ancho del Local (m)*",
    areaTotal: "Área Total del Local (m²)",
    areaOcupada: "Área Útil (m²)*"
  },
  oficina: {
    largo: "Largo de la Oficina (m)*",
    ancho: "Ancho del Oficina (m)*",
    areaTotal: "Área Total de la Oficina (m²)",
    areaOcupada: "Área Útil (m²)*"
  },
  cochera: {
    largo: "Largo de la Cochera (m)*",
    ancho: "Ancho del Cochera (m)*",
    areaTotal: "Área Total de la Cochera (m²)",
    areaOcupada: "Área Útil (m²)*"
  }
};

const capitalizeFirstLetter = (str) => {
  return str
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const InfPublish = ({ 
  formData, 
  handleInputChange, 
  handleCurrencyChange, 
  errors,
  isAutoConverting 
}) => {
  return (
    <div className="space-y-8">
      {/* Header mejorado */}
      <div className="text-center mb-8">
        <div className="relative mb-6">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="h-0.5 w-12 bg-gradient-to-r from-transparent to-emerald-500 rounded-full"></div>
            <div className="p-3 bg-gradient-to-br from-emerald-500 to-blue-600 rounded-2xl shadow-lg">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div className="h-0.5 w-12 bg-gradient-to-l from-transparent to-blue-500 rounded-full"></div>
          </div>
          
          <h2 className="text-3xl font-bold bg-gradient-to-r from-emerald-600 via-blue-600 to-purple-600 
                         bg-clip-text text-transparent mb-3">
            Información de la Propiedad
          </h2>
          
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
            <div className="w-16 h-0.5 bg-gradient-to-r from-emerald-400 to-blue-400 rounded-full"></div>
            <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse delay-75"></div>
          </div>
        </div>
        
        <p className="text-gray-600 text-lg max-w-2xl mx-auto leading-relaxed">
          Completa los datos básicos para publicar tu propiedad
        </p>
      </div>

      {/* Tipo de propiedad */}
      <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-300">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl">
            <Building2 className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-800">Tipo de Propiedad</h3>
            <p className="text-gray-500 text-sm">Selecciona qué tipo de inmueble vas a publicar</p>
          </div>
        </div>
        
        <select
          name="tipo"
          value={formData.tipo}
          onChange={handleInputChange}
          className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-100 
                     focus:border-blue-500 transition-all duration-200 text-lg font-medium
                     bg-gradient-to-r from-gray-50 to-white hover:border-blue-300"
        >
          <option value="" className="text-gray-400">Seleccione el tipo de propiedad</option>
          <option value="casa">🏠 Casa</option>
          <option value="departamento">🏢 Departamento</option>
          <option value="terreno">🌍 Terreno</option>
          <option value="local">🏪 Local Comercial</option>
          <option value="oficina">🏢 Oficina</option>
          <option value="cochera">🚗 Cochera</option>
        </select>
      </div>

      {/* Título y descripción */}
      <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-300">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl">
            <Edit3 className="h-6 w-6 text-emerald-600" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-800">Título y Descripción</h3>
            <p className="text-gray-500 text-sm">Información que verán los interesados</p>
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold text-gray-700">
                Título de la Publicación*
              </label>
              <span className={`text-xs px-2 py-1 rounded-full ${
                (formData.titulo?.length || 0) > 50 
                  ? 'bg-red-100 text-red-600' 
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {formData.titulo?.length || 0}/60
              </span>
            </div>
            <input
              type="text"
              name="titulo"
              value={formData.titulo}
              onChange={handleInputChange}
              className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-emerald-100 
                         focus:border-emerald-500 transition-all duration-200 text-lg
                         bg-gradient-to-r from-gray-50 to-white hover:border-emerald-300"
              placeholder="Ej: Casa Moderna de 3 Habitaciones en San Isidro"
              maxLength={60}
            />
            {errors?.titulo && (
              <div className="flex items-center gap-2 mt-2 text-red-500 text-sm">
                <Info className="w-4 h-4" />
                <span>{errors.titulo}</span>
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold text-gray-700">
                Descripción Detallada*
              </label>
              <span className={`text-xs px-2 py-1 rounded-full ${
                (formData.descripcion_corta?.length || 0) < 50 && (formData.descripcion_corta?.length || 0) > 0
                  ? 'bg-yellow-100 text-yellow-600' 
                  : (formData.descripcion_corta?.length || 0) >= 50
                  ? 'bg-green-100 text-green-600'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {formData.descripcion_corta?.length || 0}/500
              </span>
            </div>
            <textarea
              name="descripcion_corta"
              value={formData.descripcion_corta}
              onChange={handleInputChange}
              className="w-full p-4 border-2 border-gray-200 rounded-xl min-h-[140px] 
                         focus:ring-4 focus:ring-emerald-100 focus:border-emerald-500 
                         transition-all duration-200 resize-none
                         bg-gradient-to-r from-gray-50 to-white hover:border-emerald-300"
              placeholder="Describe las características principales: ubicación, estado, comodidades, ventajas del área..."
              maxLength={500}
            />
            {formData.descripcion_corta?.length < 50 && formData.descripcion_corta?.length > 0 && (
              <div className="flex items-center gap-2 mt-2 text-yellow-600 text-sm">
                <Info className="w-4 h-4" />
                <span>Recomendamos al menos 50 caracteres para una mejor descripción</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Precios rediseñado */}
      <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-300">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl">
            <DollarSign className="h-6 w-6 text-amber-600" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-800">
              {formData.operacion === 'comprar' ? 'Precio de Venta' : 'Precio de Alquiler'}
            </h3>
            <p className="text-gray-500 text-sm">Define el precio en soles y/o dólares</p>
          </div>
        </div>

        {/* Información del tipo de cambio */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-3">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            <div>
              <h4 className="text-sm font-semibold text-blue-800">Tipo de Cambio Referencial</h4>
              <p className="text-blue-600 text-sm">S/ 3.65 por cada USD $1.00</p>
            </div>
            {isAutoConverting && (
              <div className="ml-auto">
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 
                                text-xs font-medium rounded-full">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                  Conversión automática
                </span>
              </div>
            )}
          </div>
        </div>

        {formData.operacion === 'comprar' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Precio de Venta en Soles*
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <span className="text-amber-600 font-bold text-lg">S/</span>
                </div>
                <input
                  type="number"
                  name="precio_soles"
                  value={formData.precio_soles}
                  onChange={handleCurrencyChange}
                  className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl 
                             focus:ring-4 focus:ring-amber-100 focus:border-amber-500 
                             transition-all duration-200 text-lg font-semibold
                             bg-gradient-to-r from-amber-50 to-yellow-50"
                  placeholder="0.00"
                  min="0"
                  required
                />
              </div>
              {errors?.precio_soles && (
                <div className="flex items-center gap-2 text-red-500 text-sm">
                  <Info className="w-4 h-4" />
                  <span>{errors.precio_soles}</span>
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Precio de Venta en Dólares
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <span className="text-green-600 font-bold text-lg">$</span>
                </div>
                <input
                  type="number"
                  name="precio_dolares"
                  value={formData.precio_dolares}
                  onChange={handleCurrencyChange}
                  className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl 
                             focus:ring-4 focus:ring-green-100 focus:border-green-500 
                             transition-all duration-200 text-lg font-semibold
                             bg-gradient-to-r from-green-50 to-emerald-50"
                  placeholder="0.00"
                  min="0"
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Alquiler Mensual (Soles)*
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <span className="text-amber-600 font-bold text-lg">S/</span>
                </div>
                <input
                  type="number"
                  name="precio_mensual"
                  value={formData.precio_mensual}
                  onChange={handleCurrencyChange}
                  className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl 
                             focus:ring-4 focus:ring-amber-100 focus:border-amber-500 
                             transition-all duration-200 text-lg font-semibold
                             bg-gradient-to-r from-amber-50 to-yellow-50"
                  placeholder="0.00"
                  min="0"
                  required
                />
              </div>
              {errors?.precio_mensual && (
                <div className="flex items-center gap-2 text-red-500 text-sm">
                  <Info className="w-4 h-4" />
                  <span>{errors.precio_mensual}</span>
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Alquiler Mensual (Dólares)
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <span className="text-green-600 font-bold text-lg">$</span>
                </div>
                <input
                  type="number"
                  name="precio_dolares"
                  value={formData.precio_dolares}
                  onChange={handleCurrencyChange}
                  className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl 
                             focus:ring-4 focus:ring-green-100 focus:border-green-500 
                             transition-all duration-200 text-lg font-semibold
                             bg-gradient-to-r from-green-50 to-emerald-50"
                  placeholder="0.00"
                  min="0"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Mantenimiento (Soles)
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <span className="text-purple-600 font-bold text-lg">S/</span>
                </div>
                <input
                  type="number"
                  name="mantenimiento"
                  value={formData.mantenimiento}
                  onChange={handleInputChange}
                  className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl 
                             focus:ring-4 focus:ring-purple-100 focus:border-purple-500 
                             transition-all duration-200 text-lg font-semibold
                             bg-gradient-to-r from-purple-50 to-pink-50"
                  placeholder="0.00"
                  min="0"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InfPublish;
export { capitalizeFirstLetter, propertyLabels };